"use strict";
/**
 * Pure helpers for Focus / human duration detection.
 * Only flowKind S|T are time-tracking duration intervals.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.describeStopDurationPreview = exports.resolveFocusAccountRealm = exports.extractFocusAccountRealmFromPayload = exports.isFocusMetaStale = exports.isOpenTimerOrphan = exports.adoptFocusTLabel = exports.adoptNeedsReopen = exports.canAdoptAsFocusT = exports.activeChipsForRow = exports.isRowFocusActive = exports.activeRolesForRow = exports.isFocusPointer = exports.isRowAlreadyDone = exports.isRowOpen = exports.isTimeTrackingRow = exports.rowFlowKind = exports.isTimeTrackingFlow = exports.normalizeFlowKind = void 0;
function normalizeFlowKind(flow) {
    return String(flow !== null && flow !== void 0 ? flow : '')
        .trim()
        .toUpperCase();
}
exports.normalizeFlowKind = normalizeFlowKind;
/** Human duration interval (Focus S/T). Not stamps, notes, L, KB. */
function isTimeTrackingFlow(flow) {
    var k = normalizeFlowKind(flow);
    return k === 'T' || k === 'S';
}
exports.isTimeTrackingFlow = isTimeTrackingFlow;
function rowFlowKind(row) {
    if (!row)
        return '';
    var raw = row.pnFlowKind != null ? row.pnFlowKind : row.flowKind;
    return normalizeFlowKind(raw);
}
exports.rowFlowKind = rowFlowKind;
/** Human duration interval: explicit flowKind S or T only (no task/topic guess). */
function isTimeTrackingRow(row) {
    return isTimeTrackingFlow(rowFlowKind(row));
}
exports.isTimeTrackingRow = isTimeTrackingRow;
function isRowOpen(row) {
    try {
        var v = row && row.pnCompletedUTC;
        if (v === null || v === undefined)
            return true;
        if (typeof v === 'string') {
            var s = v.trim();
            if (!s)
                return true;
            if (/^0{4}-0{2}-0{2}/.test(s))
                return true;
            return false;
        }
        // Date object or other non-empty value → completed
        if (v instanceof Date)
            return Number.isNaN(v.getTime());
        return false;
    }
    catch (_a) {
        return true;
    }
}
exports.isRowOpen = isRowOpen;
/**
 * Fully done for UI: has a real completed time **and** done-by person.
 * Used to no-op / disable the Done menu action (re-done must not re-stamp).
 */
function isRowAlreadyDone(row) {
    if (!row || isRowOpen(row))
        return false;
    var by = row.pnCompletedPersonPK != null ? row.pnCompletedPersonPK : row.CompletedPersonPK;
    if (by === null || by === undefined || by === '')
        return false;
    var n = Number(by);
    if (!Number.isNaN(n) && n === 0)
        return false;
    return true;
}
exports.isRowAlreadyDone = isRowAlreadyDone;
function isFocusPointer(row, openId) {
    if (!row || openId == null || row.ID == null)
        return false;
    return Number(row.ID) === Number(openId);
}
exports.isFocusPointer = isFocusPointer;
var ACTIVE_CHIP_META = {
    focus_t: {
        label: 'Focus T',
        title: 'Active Focus T — current activity timer (person_focus_state)',
        cssClass: 'focus-active-chip focus-active-chip--t',
    },
    focus_s: {
        label: 'Focus S',
        title: 'Active Focus S — current session envelope',
        cssClass: 'focus-active-chip focus-active-chip--s',
    },
    open_orphan: {
        label: 'Open',
        title: 'Open S|T timer not pointed by Focus (explicit flowKind only)',
        cssClass: 'focus-active-chip focus-active-chip--orphan',
    },
};
/**
 * Ordered roles for a row given current Focus T/S pointer ids.
 * Focus T/S chips and orphan stop UI only when the row itself has explicit flowKind S|T.
 * (Pointer alone is not enough — otherwise non-timer notes could show seal/stop actions.)
 */
function activeRolesForRow(row, pointers) {
    var roles = [];
    var tId = pointers === null || pointers === void 0 ? void 0 : pointers.focusTId;
    var sId = pointers === null || pointers === void 0 ? void 0 : pointers.focusSId;
    var isTimer = isTimeTrackingRow(row);
    if (isTimer && isFocusPointer(row, tId)) {
        roles.push('focus_t');
    }
    if (isTimer && isFocusPointer(row, sId)) {
        roles.push('focus_s');
    }
    if (isOpenTimerOrphan(row, tId, sId)) {
        roles.push('open_orphan');
    }
    return roles;
}
exports.activeRolesForRow = activeRolesForRow;
function isRowFocusActive(row, pointers) {
    return activeRolesForRow(row, pointers).some(function (r) { return r === 'focus_t' || r === 'focus_s'; });
}
exports.isRowFocusActive = isRowFocusActive;
/** UI chips for search/recent rows — only roles returned by activeRolesForRow. */
function activeChipsForRow(row, pointers, opts) {
    var includeOrphan = (opts === null || opts === void 0 ? void 0 : opts.includeOrphan) !== false;
    return activeRolesForRow(row, pointers)
        .filter(function (role) { return includeOrphan || role !== 'open_orphan'; })
        .map(function (role) { return (__assign({ role: role }, ACTIVE_CHIP_META[role])); });
}
exports.activeChipsForRow = activeChipsForRow;
/**
 * Whether the row can become Focus T via adopt-t (not already the T pointer).
 * Only explicit flowKind **T** (Task) — not S, not unstamped notes.
 * Open T orphans and completed T (resume with reopen) qualify.
 */
function canAdoptAsFocusT(row, pointers) {
    if (!row || row.ID == null || row.ID === '')
        return false;
    if (rowFlowKind(row) !== 'T')
        return false;
    if (isFocusPointer(row, pointers === null || pointers === void 0 ? void 0 : pointers.focusTId))
        return false;
    return true;
}
exports.canAdoptAsFocusT = canAdoptAsFocusT;
/** True when adopt should clear finish time (completed row → resume). */
function adoptNeedsReopen(row) {
    return !isRowOpen(row);
}
exports.adoptNeedsReopen = adoptNeedsReopen;
/** Menu caption for adopt. */
function adoptFocusTLabel(row) {
    if (adoptNeedsReopen(row))
        return 'Resume as current task';
    if (isTimeTrackingRow(row) && isRowOpen(row))
        return 'Mark as current task';
    return 'Mark as current task';
}
exports.adoptFocusTLabel = adoptFocusTLabel;
/**
 * Open orphan = incomplete row with **explicit** flowKind S|T that is not Focus T/S.
 * No flowKind (or I/L/KB/…) ⇒ not a time record ⇒ never an orphan.
 */
function isOpenTimerOrphan(row, openMetaOpenId, sessionMetaOpenId) {
    if (!row || !isRowOpen(row) || !isTimeTrackingRow(row))
        return false;
    if (isFocusPointer(row, openMetaOpenId) || isFocusPointer(row, sessionMetaOpenId))
        return false;
    return true;
}
exports.isOpenTimerOrphan = isOpenTimerOrphan;
/** Stale open Focus meta (seconds open vs threshold). */
function isFocusMetaStale(openSec, kind, tLimitSec, sLimitSec) {
    if (kind === void 0) { kind = 'T'; }
    if (tLimitSec === void 0) { tLimitSec = 4 * 3600; }
    if (sLimitSec === void 0) { sLimitSec = 12 * 3600; }
    if (!openSec || openSec <= 0)
        return false;
    return openSec >= (kind === 'S' ? sLimitSec : tLimitSec);
}
exports.isFocusMetaStale = isFocusMetaStale;
function pickFocusContextValue() {
    var vals = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        vals[_i] = arguments[_i];
    }
    for (var _a = 0, vals_1 = vals; _a < vals_1.length; _a++) {
        var v = vals_1[_a];
        if (v == null)
            continue;
        var s = String(v).trim();
        if (s && s !== '-' && s !== 'null' && s !== 'undefined')
            return s;
    }
    return null;
}
/**
 * Server focus signal: person_focus_state account/realm as returned by
 * /api/v2/focus/* and PHP recent openMeta/sessionMeta.
 * Prefer top-level, then open T meta, then session S meta.
 */
function extractFocusAccountRealmFromPayload(data) {
    var _a, _b, _c, _d;
    if (!data)
        return { account: null, realm: null };
    return {
        account: pickFocusContextValue(data.account, (_a = data.openMeta) === null || _a === void 0 ? void 0 : _a.account, (_b = data.sessionMeta) === null || _b === void 0 ? void 0 : _b.account),
        realm: pickFocusContextValue(data.realm, (_c = data.openMeta) === null || _c === void 0 ? void 0 : _c.realm, (_d = data.sessionMeta) === null || _d === void 0 ? void 0 : _d.realm),
    };
}
exports.extractFocusAccountRealmFromPayload = extractFocusAccountRealmFromPayload;
/**
 * Request body / parent-row context when starting Focus from a schedule/note.
 * Prefer row asset fields, then person fields, then existing API meta.
 */
function resolveFocusAccountRealm(row, data) {
    var fromPayload = extractFocusAccountRealmFromPayload(data);
    return {
        account: pickFocusContextValue(row === null || row === void 0 ? void 0 : row.pnAssetAccount, row === null || row === void 0 ? void 0 : row.pnAccount, fromPayload.account),
        realm: pickFocusContextValue(row === null || row === void 0 ? void 0 : row.pnAssetRealm, row === null || row === void 0 ? void 0 : row.pnUserRealm, fromPayload.realm),
    };
}
exports.resolveFocusAccountRealm = resolveFocusAccountRealm;
function describeStopDurationPreview(startUtc, durationMinutes, localTimeLabel) {
    var mins = Number(durationMinutes) || 0;
    if (mins <= 0)
        return '';
    var startMs = null;
    if (startUtc) {
        var text = String(startUtc).trim().replace(' ', 'T');
        if (text && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)) {
            text = text + 'Z';
        }
        var ms = Date.parse(text);
        startMs = Number.isNaN(ms) ? null : ms;
    }
    if (startMs == null) {
        return "".concat(mins, "m from unknown start");
    }
    var endMs = startMs + mins * 60 * 1000;
    var startLabel = localTimeLabel ||
        new Date(startMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    var endLabel = new Date(endMs).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    });
    var durLabel = mins < 60 ? "".concat(mins, "m") : mins % 60 === 0 ? "".concat(mins / 60, "h") : "".concat(Math.floor(mins / 60), "h ").concat(mins % 60, "m");
    return "".concat(startLabel, " \u2192 ").concat(endLabel, " (").concat(durLabel, ")");
}
exports.describeStopDurationPreview = describeStopDurationPreview;
